import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type ListModuleCatalogueResult } from '@whinerp/client';
import {
  EmptyState,
  ErrorState,
  Icon,
  Num,
  PageHeading,
  Panel,
  SectionLabel,
  Skeleton,
} from '@whinerp/ui';
import { PlanChange } from './plan-change.js';
import { UsageBars } from '../shared/usage-bars.js';
import { formatCount } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { useSession } from '../../app/session.js';
import { defineScreen } from '../types.js';

/**
 * SC-35 Plan and usage.
 *
 * Locked features are listed, with the tier that would unlock them. Hiding
 * what a plan does not include means nobody ever discovers the reason to
 * upgrade - and, worse, an administrator cannot tell "we switched that off"
 * from "we do not have that", which are different conversations with different
 * people.
 */

type Catalogue = ListModuleCatalogueResult;

function PlanScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const { session } = useSession();

  const catalogue = useQuery<Catalogue, ApiError>({
    queryKey: ['modules'],
    queryFn: () => api.listModuleCatalogue({}),
  });

  if (catalogue.isPending) return <PlanLoading />;
  if (catalogue.isError) {
    return <PlanError error={catalogue.error} retry={() => void catalogue.refetch()} />;
  }

  const features = catalogue.data.modules.flatMap((module) =>
    module.features.map((feature) => ({ ...feature, moduleLabel: module.label })),
  );
  const locked = features.filter((feature) => feature.state === 'plan-locked');
  const included = features.filter((feature) => feature.state !== 'plan-locked');

  return (
    <div data-component="PlanAndUsage" className="flex w-full flex-col gap-4">
      <PageHeading>{t('setup.plan.title')}</PageHeading>

      <Panel data-region="plan">
        <div className="flex flex-wrap items-baseline gap-4">
          <span className="flex flex-col">
            <SectionLabel>{t('setup.plan.current')}</SectionLabel>
            <span className="font-serif text-h2 text-ink">{session.workspace.planLabel}</span>
          </span>
          <span className="flex flex-col">
            <SectionLabel>{t('setup.plan.included')}</SectionLabel>
            <Num className="font-serif text-h2 text-ink">{formatCount(included.length, format)}</Num>
          </span>
          <span className="flex flex-col">
            <SectionLabel>{t('setup.plan.lockedCount')}</SectionLabel>
            <Num className="font-serif text-h2 text-ink-meta">{formatCount(locked.length, format)}</Num>
          </span>
        </div>
      </Panel>

      {/* Visible, with a lock and the tier. Never hidden. */}
      <Panel data-region="locked">
        <div className="flex flex-col gap-2">
          <SectionLabel>{t('setup.plan.upgrade')}</SectionLabel>
          {locked.length === 0 ? (
            <p className="text-body text-ink-meta">{t('setup.plan.included')}</p>
          ) : (
            <ul className="flex flex-col">
              {locked.map((feature) => (
                <li
                  key={feature.key}
                  data-locked-feature={feature.key}
                  className="flex flex-wrap items-center gap-3 border-t border-t-rule py-1 first:border-t-0"
                >
                  <Icon name="Lock" size="inline" decorative />
                  <span className="min-w-0 flex-1 truncate text-dense text-ink">{feature.label}</span>
                  <span className="text-meta text-ink-meta">{feature.moduleLabel}</span>
                  <span
                    data-required-tier={feature.planMin}
                    className="inline-flex h-chip items-center rounded-chip bg-bronze-tint px-2 text-meta text-bronze-text"
                  >
                    {t('setup.plan.locked', { values: { plan: feature.planMinLabel } })}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Panel>

      {/* Moving between plans, and what a downgrade costs. */}
      <PlanChange />

      <Panel data-region="usage">
        <div className="flex flex-col gap-2">
          <SectionLabel>{t('setup.plan.usage')}</SectionLabel>
          {/* Real meters now (F-143). Every one counts something the
              database can answer for; the ones the criteria name that nothing
              counts - API calls - are absent rather than invented. */}
          <UsageBars />
        </div>
      </Panel>
    </div>
  );
}

function PlanEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="CreditCard"
      title={t('setup.plan.title')}
      body={t('setup.plan.included')}
      primary={{ label: t('empty.action.seePlans'), icon: 'CreditCard', href: '/setup/plan' }}
    />
  );
}

function PlanLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full flex-col gap-4" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <Skeleton className="h-line-h1 w-block-lg" />
      <Skeleton className="h-block-sm w-full" />
      <Skeleton className="h-block-md w-full" />
    </div>
  );
}

function PlanError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="Your plan could not be loaded"
      body={error instanceof Error ? error.message : 'It did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-35',
  route: '/setup/plan',
  surface: 'page',
  title: 'Plan and usage',
  // F-013, not F-014. This screen reads the entitlement catalogue; metering -
  // which is what F-014 is - has no operation behind it yet, and claiming a
  // feature whose data does not exist is exactly the half-built claim the
  // checks are here to refuse.
  features: ['F-013', 'F-142', 'F-143'],
  entitlement: 'always-on',
  reachedBy: [{ kind: 'screen', from: 'SC-30', how: 'the Plan card in Setup' }],
  // No UsageMeter: metering is not built, and declaring a component this
  // screen cannot honestly render would make check:screens vouch for a gauge
  // with nothing behind it.
  components: [],
  elements: ['PlanAndUsage'],
  states: {
    empty: PlanEmpty,
    loading: PlanLoading,
    error: PlanError,
  },
  component: PlanScreen,
});

export default screen;
